import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StorefrontService } from "src/app/services/StorefrontService";
import { Basket, money, ProductType } from "./Basket";
import { clubTheme } from "./ClubTheme";
import { FanSession } from "./FanSession";
import "./Shop.css";
import { ShopHeader } from "./ShopHeader";
import { ShopNav } from "./ShopNav";

const term = (months: number) => {
	if (months === 12) return "a year";
	if (months === 1) return "a month";
	if (months % 12 === 0) return `${months / 12} years`;
	return `${months} months`;
};

/**
 * Joining the club.
 *
 * Not a matchday purchase — somebody joins once a year, usually from a link,
 * so it gets its own tab rather than sitting under fixtures.
 *
 * A membership belongs to a person, so this is the one thing in the shop that
 * genuinely needs an account. Rather than let somebody fill a basket and be
 * refused at the till, the requirement is stated on the button.
 */
export function ShopJoin() {
	const { slug = "" } = useParams();
	const navigate = useNavigate();
	const [data, setData] = React.useState<any>(null);
	const [loading, setLoading] = React.useState(true);

	React.useEffect(() => {
		let live = true;
		(async () => {
			try {
				const res = await StorefrontService.getMemberships(slug);
				if (live && res.ok) setData(await res.json());
			} catch {
				// The empty state covers it.
			} finally {
				if (live) setLoading(false);
			}
		})();
		return () => { live = false; };
	}, [slug]);

	const signedIn = FanSession.signedIn();

	const join = (product: any) => {
		if (!signedIn) {
			navigate(`/club/${slug}/account?next=/club/${slug}/join`);
			return;
		}
		Basket.add(slug, {
			name: product.name,
			occasion: "Membership",
			pricePence: product.pricePence,
			productId: product.productId,
			productType: ProductType.Membership,
			quantity: 1,
			ticketTypeId: 0,
			when: ""
		});
		navigate(`/club/${slug}/basket`);
	};

	return (
		<div className="shop has-nav" style={clubTheme(data?.club)}>
			<ShopHeader club={data?.club} slug={slug} />
			<div className="shop-inner">
				<h2 className="shop-section-title">Join the club</h2>

				{loading && <div className="shop-result"><span className="shop-spinner" /></div>}

				{!loading && !data?.memberships?.length &&
					<div className="shop-result">
						<h2>No memberships yet</h2>
						<p>{data?.club?.name || "This club"} isn&rsquo;t offering memberships at the moment.</p>
					</div>}

				{!loading && data?.memberships?.map((m: any) => (
					<div className="shop-package" key={m.productId}>
						<div className="shop-package-head">
							<strong>{m.name}</strong>
							<span className="shop-price">{money(m.pricePence)}</span>
						</div>
						{m.description && <p className="shop-package-desc">{m.description}</p>}
						<div className="shop-package-meta">
							<span>Runs for {term(m.durationMonths)}</span>
							{m.grantsPrioritySale && <span className="shop-included">Priority on match tickets</span>}
						</div>
						<button className="shop-btn" onClick={() => join(m)} type="button">
							{signedIn ? `Join · ${money(m.pricePence)}` : "Sign in to join"}
						</button>
					</div>
				))}

				{!loading && data?.memberships?.length > 0 && !signedIn &&
					<p className="shop-note">
						A membership belongs to you rather than to an order, so it needs an account.
					</p>}
			</div>
			<ShopNav slug={slug} />
		</div>
	);
}
